// Stripe billing configuration (client side).
//
// LIVE-mode Payment Links since 2026-08-31. Payment Links and the portal login
// link are public URLs (they are meant to be shared), so they are safe to keep
// in git. Secrets (sk_..., whsec_...) live ONLY in Firebase Functions secrets.
//
// THESE LINKS TAKE REAL MONEY. The rest of the Stripe wiring has to be live in
// lockstep or a real payment goes unfulfilled: `STRIPE_SECRET` must be the
// `sk_live_...` key (every callable here reads it — refreshSubscription,
// createPortalSession, deleteAccount — and a test key cannot see a live
// customer at all), and there must be a LIVE webhook endpoint whose
// `whsec_...` is in `STRIPE_WEBHOOK_SECRET`, or `checkout.session.completed`
// fails signature verification and nobody is ever upgraded.
// The live-mode checklist is in launch/TODO.md.

export const STRIPE_PAYMENT_LINKS = {
    // Fiwara Pro — ANNUAL. L5: this must be the €89 LIST-price link with
    // `allow_promotion_codes: true`, not the old €49 one. The €49 founder price
    // is a promotion code applied on top (see OFFER below), so one link serves
    // both the founder and post-founder states and there is never a link swap.
    yearly: 'https://buy.stripe.com/14AbJ24V2bPeegUf7p7ss03',
    // Fiwara Pro — €9/mo standard. Promotion codes are left ENABLED on this link
    // (owner decision 2026-08-31): FOUNDER49 is scoped to the yearly product and
    // is only ever meant for it. Verified the same day that Stripe rejects
    // FOUNDER49 on this checkout — "the order does not meet the minimum
    // quantity" — so the €40-off coupon cannot reach a €9 invoice and zero it,
    // which was the original worry.
    //
    // Note WHAT is protecting you, though: a minimum-AMOUNT rule on the
    // promotion code, not product scoping. The guard is tied to the size of the
    // order rather than to which product it is for, so re-test FOUNDER49
    // against this link after any pricing change — a cheaper annual plan, a
    // regional price, or a second discount could lift a monthly order over that
    // minimum, and the block would disappear without a word.
    monthly: 'https://buy.stripe.com/9B64gA9bidXm0q42kD7ss02',
};

/**
 * L5 — the founder offer, as one descriptor the whole app renders off.
 *
 * The problem this replaces: the founder price was promised in four places with
 * three different meanings ("locked forever", "for life", "held while
 * subscribed"), struck through an €89 anchor that had never been charged (an
 * EU Omnibus Directive problem), and capped at "the first hundred people" with
 * no counter and no code anywhere that knew the subscriber count.
 *
 * The fix moves enforcement out of copy and into Stripe:
 *
 *   • a coupon `FOUNDER49` — €40 off, `duration=forever`, `max_redemptions=100`
 *     — exposed as a promotion code. `duration=forever` means the discount
 *     rides every renewal of THAT subscription, so "locked" is enforced by
 *     Stripe rather than remembered by us, and cancel-then-resubscribe lands on
 *     €89 by construction, which is exactly what the copy now says.
 *   • `max_redemptions: 100` makes the cap real: buyer 101 pays €89 with no
 *     manual switch and no deploy.
 *   • `applies_to[products]` restricted to the ANNUAL product, so a portal
 *     yearly→monthly switch drops the discount instead of zeroing a €9 invoice.
 *
 * PRECONDITION for that restriction: annual must be its own Stripe *product*,
 * not a second price under the shared one. Check the Dashboard when wiring this.
 *
 * The app's job is now only presentation, from this one object.
 */
export const OFFER = {
    active: true,                  // kill switch, independent of the count
    name: 'Founder price',         // human label, for copy fallback + logs
    code: 'FOUNDER49',             // null = no offer running
    copyKey: 'founder',            // which locale copy set to render
    plans: ['yearly'],             // which STRIPE_PAYMENT_LINKS keys it applies to
    limit: 100,                    // null = uncapped in-app; Stripe still caps
    counter: {                     // null = no counter, no Firestore read at all
        source: 'paidUsersEver',   // | 'redemptions'
        baseline: 0,               // count this offer starts from
        showFrom: 25,              // hide the line below this many
    },
};

// The public doc the webhook maintains the seat count on. Readable by anyone,
// writable by nobody (see firestore.rules) — it is on the landing page, which
// signed-out visitors see.
export const PUBLIC_STATS_DOC = 'public/stats';

// No-code Stripe customer portal login link, LIVE mode since 2026-08-31. Only a fallback:
// the primary path is the `createPortalSession` callable, which hands an
// authenticated subscriber straight into their portal with no email step.
//
// H6 history, so nobody re-audits it: the old sandbox value shared its token
// with the sandbox yearly Payment Link, which looks exactly like a copy-paste
// error and was reported as one on 2026-08-29. It was not — Stripe had minted
// the same token for two different objects, and the URL loaded a working portal.
// The live pair below do not share a token, so the coincidence is gone.
//
// Verified 2026-08-31: loads "Fakturace Fiwara" with no sandbox banner and no
// "account is not activated" notice — both of which the test link showed.
export const STRIPE_PORTAL_URL = 'https://billing.stripe.com/p/login/00w7sMfzGcTiegU4sL7ss00';

/**
 * Which of the three plan lines to show a paying subscriber.
 *
 * One helper, used by both Settings and the Plan page, because those two had the
 * same expression copy-pasted and it was wrong in both: founder status was
 * inferred from `priceInterval === 'year'`, so EVERY annual subscriber was told
 * "Founder price €49/yr — locked forever" — including buyer 101 paying €89, and
 * including a founder who had switched away and back and lost the discount.
 *
 * The discount on the subscription is the fact; the interval is a coincidence.
 * "Locked forever" is claimed only when Stripe's own coupon duration says
 * forever, so the app cannot promise a lock the billing system is not keeping.
 *
 * Returns a locale key: 'founder' | 'annual' | 'monthly'.
 */
export const planNoteKey = ({ priceInterval, discountCode, discountDuration }) => {
    // The interval is required as well as the discount, not instead of it. The
    // founder line names an ANNUAL price, so showing it to a monthly subscriber
    // would state the wrong number even though the coupon is genuinely theirs.
    // FOUNDER49 is annual-scoped so this should not arise — "should not arise"
    // is exactly the reasoning that produced the bug this helper replaced.
    const isFounder = priceInterval === 'year'
        && discountCode === OFFER.code
        && discountDuration === 'forever';
    if (isFounder) return 'billing.settings.founderNote';
    return priceInterval === 'month'
        ? 'billing.settings.monthlyNote'
        : 'billing.settings.annualNote';
};

// Freemium gate limits (Pro = runs slider max 5000, snapshots capped at
// PRO_SNAPSHOT_LIMIT below).
//
// `monteCarloRuns` is the free teaser, lowered from 100 to 5 in the
// post-release pass. Every surface that names the number reads it from here —
// the slider max and its hard re-check in SimulationPage, the Plan page's free
// column, the paywall's reason line and the landing pricing bullet — so this
// constant is the only place it is stated.
export const FREE_LIMITS = {
    snapshots: 3,
    monteCarloRuns: 5,
};

// Pro's snapshot cap. Pro is NOT unlimited — every surface that names the cap
// must say 50, not "unlimited".
export const PRO_SNAPSHOT_LIMIT = 50;

// After signup/login we may want to reopen the paywall (the visitor picked
// Pro on the landing pricing card, or hit a gate in demo mode and went off to
// create an account). localStorage with an expiry survives the auth redirect.
const POST_AUTH_PAYWALL_KEY = 'fiwara_postAuthPaywall_v1';
const POST_AUTH_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

export const rememberPostAuthPaywall = (source) => {
    try {
        localStorage.setItem(POST_AUTH_PAYWALL_KEY, JSON.stringify({ source, ts: Date.now() }));
    } catch { /* storage unavailable — intent is best-effort */ }
};

export const consumePostAuthPaywall = () => {
    try {
        const raw = localStorage.getItem(POST_AUTH_PAYWALL_KEY);
        if (!raw) return null;
        localStorage.removeItem(POST_AUTH_PAYWALL_KEY);
        const { source, ts } = JSON.parse(raw);
        if (!ts || Date.now() - ts > POST_AUTH_MAX_AGE_MS) return null;
        return source || 'landing_pricing';
    } catch {
        return null;
    }
};

export const isBillingConfigured = () =>
    Boolean(STRIPE_PAYMENT_LINKS.yearly && STRIPE_PAYMENT_LINKS.monthly);

/** Does the founder offer apply to this plan, given how many seats are taken? */
export const isOfferActiveFor = (planKey, taken) => {
    if (!OFFER.active || !OFFER.code) return false;
    if (!OFFER.plans.includes(planKey)) return false;
    if (OFFER.limit != null && taken != null && taken >= OFFER.limit) return false;
    return true;
};

// Hosted-checkout hand-off: Stripe Payment Links accept these query params;
// client_reference_id comes back on checkout.session.completed and is how the
// webhook knows which Firebase user to upgrade.
//
// L5 — CALL THIS AT CLICK TIME, never at render time. A page left open while
// the last founder seat goes would otherwise carry a promo param for a code
// Stripe has since deactivated, and the user would meet an error on the
// checkout page instead of a price. Building the URL when the button is pressed
// means the seat count it reads is the one that was true a moment ago.
export const buildCheckoutUrl = (planKey, uid, email, { taken = null } = {}) => {
    const base = STRIPE_PAYMENT_LINKS[planKey];
    if (!base || !uid) return null;
    const params = new URLSearchParams({ client_reference_id: uid });
    if (email) params.set('prefilled_email', email);
    if (isOfferActiveFor(planKey, taken)) params.set('prefilled_promo_code', OFFER.code);
    return `${base}?${params.toString()}`;
};
